/** @file puppet.import: replaces the document's puppet with a whole validated puppet document. */
import { validatePuppet } from '@zilo/pose3d-format/validate/validatePuppet';
import { isId } from '@zilo/pose3d-std/ids/isId';
import type { CommandDef } from '../apply/commandDef';
import { puppetInverse } from '../apply/puppetInverse';
import { orderNodes } from '../node/orderNodes';
import { readInput } from '../validate/readInput';
import { withPathPrefix } from '../validate/withPathPrefix';

/**
 * `puppet.import { doc }` → `{ id }`: `doc` is validated (errors under `/input/doc`) and replaces the puppet; its id
 * is kept when it is a `pup` id, else a fresh one is made. Inverse: re-import the previous puppet.
 */
export const puppetImportCommand: CommandDef<'puppet.import'> = {
  name: 'puppet.import',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => ({ doc: readInput(input, path, ['doc']).raw('doc') }),
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const inverse = puppetInverse(draft);
    const doc = structuredClone(input.doc);
    if (typeof doc === 'object' && doc !== null && !Array.isArray(doc) && !isId((doc as { id?: unknown }).id, 'pup')) {
      (doc as { id: string }).id = ctx.makeId('pup');
    }
    draft.puppet = withPathPrefix('/input/doc', () => validatePuppet(doc, { profiles: ctx.profiles }));
    draft.puppet.nodes = orderNodes(draft.puppet.nodes);
    return { result: { id: draft.puppet.id }, warnings: [], inverse };
  },
};
